/* Predictive search (OUTLYR SAP Rebuild, 2026-09-16).
 *
 * Drives the search overlay: opens it, queries /search/suggest.json as
 * the customer types, and renders suggestions.
 *
 * These are SUGGESTIONS, not the canonical result set. Enter submits the
 * real form to /search, which renders server-side. That distinction is
 * why building markup in JS is acceptable here and nowhere else in this
 * theme.
 *
 * Money: suggest.json gives `price` in minor units. The format template
 * comes from a data attribute Liquid filled with a real formatted price,
 * and only the digits are substituted -- no currency symbol or decimal
 * convention is hardcoded here.
 */
(() => {
  const overlay = document.querySelector('[data-search-overlay]');
  if (!overlay) return;

  const form = overlay.querySelector('[data-search-form]');
  const input = overlay.querySelector('[data-search-input]');
  const results = overlay.querySelector('[data-search-results]');
  const root = window.Shopify?.routes?.root || '/';

  const open = () => {
    if (overlay.open) return;
    overlay.showModal();
    /* Focus after the dialog is shown, or the caret lands nowhere. A
       frame's delay also lets the open transition start before the
       mobile keyboard slides up over it. */
    requestAnimationFrame(() => input.focus());
  };

  const close = () => {
    if (overlay.open) overlay.close();
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-search-open]');
    if (trigger) {
      /* Real link to /search, so leave modified clicks alone. */
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      open();
      return;
    }

    if (event.target.closest('[data-search-close]')) close();
  });

  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) close();
  });

  /* Clear results when the dialog closes, so reopening never flashes the
     previous query's suggestions before the new ones arrive. */
  overlay.addEventListener('close', () => {
    results.innerHTML = '';
    form.reset();
  });

  if (!input.hasAttribute('data-predictive')) return;

  const money = (minor) => {
    const template = input.dataset.moneyFormat || '{{amount}}';
    return template.replace(/[\d.,]+/, (Number(minor) / 100).toFixed(2));
  };

  const escape = (value) => {
    const el = document.createElement('div');
    el.textContent = value ?? '';
    return el.innerHTML;
  };

  const render = (data) => {
    const groups = [
      ['Products', data.resources?.results?.products || []],
      ['Pages', data.resources?.results?.pages || []],
      ['Articles', data.resources?.results?.articles || []],
    ].filter(([, items]) => items.length);

    if (!groups.length) {
      results.innerHTML = `<p class="search-overlay__empty">No results for “${escape(input.value)}”.</p>`;
      return;
    }

    results.innerHTML = groups
      .map(([label, items]) => {
        const rows = items
          .map((item) => {
            /* Products carry an image and a price; pages and articles
               carry neither, so both are conditional rather than assumed. */
            const image = item.featured_image?.url
              ? `<img class="search-result__image" src="${escape(item.featured_image.url)}" alt="" loading="lazy" width="80" height="80">`
              : '<span class="search-result__image search-result__image--blank"></span>';

            const price =
              item.price_min !== undefined
                ? `<span class="search-result__price">${escape(money(item.price_min))}</span>`
                : '';

            return `
              <li>
                <a class="search-result" href="${escape(item.url)}">
                  ${image}
                  <span class="search-result__title">${escape(item.title)}</span>
                  ${price}
                </a>
              </li>`;
          })
          .join('');

        return `
          <div class="search-overlay__group">
            <p class="search-overlay__group-label">${label}</p>
            <ul class="search-overlay__list">${rows}</ul>
          </div>`;
      })
      .join('');
  };

  let timer;
  let controller;

  input.addEventListener('input', () => {
    clearTimeout(timer);
    const query = input.value.trim();

    if (query.length < 2) {
      results.innerHTML = '';
      return;
    }

    /* Debounced so a fast typist fires one request, not one per key. */
    timer = setTimeout(async () => {
      /* Abort the in-flight request before starting another, so a slow
         earlier response cannot land after a newer one and show stale
         results for a query the customer has already moved past. */
      controller?.abort();
      controller = new AbortController();

      try {
        const url = `${root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,page,article&resources[limit]=6`;
        const data = await fetch(url, { signal: controller.signal }).then((r) => r.json());
        render(data);
      } catch (error) {
        /* An abort is the expected path when typing continues -- only a
           genuine failure should clear the list. */
        if (error.name !== 'AbortError') results.innerHTML = '';
      }
    }, 200);
  });
})();
